import React, { useState, useEffect } from 'react';
import { getAccounts } from '../services/accounts';
import {
  getAccountPrompts,
  updateAccountPrompt,
  resetAccountPrompt,
  toggleAccountOperation
} from '../services/superadmin';
import './PromptsPage.css';

const PromptsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [error, setError] = useState('');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccountId) {
      loadAccountPrompts(selectedAccountId);
    } else {
      setOperations([]);
    }
  }, [selectedAccountId]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getAccounts(0, 100);
      setAccounts(data.accounts || []);
    } catch (err) {
      setError('Error al cargar las cuentas');
    } finally {
      setLoading(false);
    }
  };

  const loadAccountPrompts = async (accountId) => {
    try {
      setLoadingPrompts(true);
      setError('');
      const data = await getAccountPrompts(accountId);
      setOperations(data);
    } catch (err) {
      setError('Error al cargar los prompts de la cuenta');
      setOperations([]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleAccountChange = (e) => {
    const accountId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedAccountId(accountId);
    setEditingPrompt(null);
    setPromptValue('');
  };

  const handleEditPrompt = (operation) => {
    setEditingPrompt(operation.operation_type_id);
    setPromptValue(operation.custom_prompt || operation.default_prompt || '');
  };

  const handleCancelEdit = () => {
    setEditingPrompt(null);
    setPromptValue('');
  };

  const handleSavePrompt = async (operationId) => {
    if (!selectedAccountId) return;
    setSavingPrompt(true);
    try {
      await updateAccountPrompt(selectedAccountId, operationId, promptValue);
      await loadAccountPrompts(selectedAccountId);
      setEditingPrompt(null);
      setPromptValue('');
    } catch (err) {
      setError('Error al guardar el prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleResetPrompt = async (operationId) => {
    if (!selectedAccountId) return;
    if (!window.confirm('¿Resetear al prompt por defecto?')) return;

    setSavingPrompt(true);
    try {
      await resetAccountPrompt(selectedAccountId, operationId);
      await loadAccountPrompts(selectedAccountId);
    } catch (err) {
      setError('Error al resetear el prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleToggleOperation = async (operationId, currentEnabled) => {
    if (!selectedAccountId) return;
    try {
      await toggleAccountOperation(selectedAccountId, operationId, !currentEnabled);
      await loadAccountPrompts(selectedAccountId);
    } catch (err) {
      setError('Error al cambiar el estado de la operación');
    }
  };

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);

  if (loading) {
    return <div className="prompts-page"><p>Cargando...</p></div>;
  }

  return (
    <div className="prompts-page">
      <div className="page-header">
        <h1>Prompts de Operaciones</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="account-selector">
        <label htmlFor="account-select">Cuenta:</label>
        <select
          id="account-select"
          value={selectedAccountId || ''}
          onChange={handleAccountChange}
        >
          <option value="">Seleccionar cuenta...</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name} (ID: {account.id})
            </option>
          ))}
        </select>
      </div>

      {!selectedAccountId ? (
        <p className="section-description">
          Selecciona una cuenta para ver y editar sus prompts personalizados.
        </p>
      ) : loadingPrompts ? (
        <p>Cargando prompts...</p>
      ) : (
        <>
          <p className="section-description">
            Prompts para <strong>{selectedAccount?.name}</strong>.
            Las operaciones activas se ejecutan durante el análisis.
            Si el prompt es personalizado, se usa ese; si no, se usa el prompt por defecto.
          </p>

          <div className="prompts-list">
            {operations.map((operation) => (
              <div
                key={operation.operation_type_id}
                className={`prompt-card ${!operation.enabled ? 'disabled' : ''}`}
              >
                <div className="prompt-card-header">
                  <div className="prompt-card-title">
                    <h3>{operation.operation_name}</h3>
                    <span className="operation-key">{operation.operation_key}</span>
                  </div>
                  <div className="prompt-card-status">
                    <button
                      className={`status-toggle ${operation.enabled ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleOperation(operation.operation_type_id, operation.enabled)}
                      title={operation.enabled ? 'Desactivar operación' : 'Activar operación'}
                    >
                      {operation.enabled ? 'Activa' : 'Inactiva'}
                    </button>
                    <span className={`prompt-badge ${operation.has_custom_prompt ? 'custom' : 'default'}`}>
                      {operation.has_custom_prompt ? 'Personalizado' : 'Default'}
                    </span>
                  </div>
                </div>

                {editingPrompt === operation.operation_type_id ? (
                  <div className="prompt-editor">
                    <textarea
                      value={promptValue}
                      onChange={(e) => setPromptValue(e.target.value)}
                      rows={15}
                      placeholder="Escribe el prompt para esta operación..."
                    />
                    <div className="prompt-actions">
                      <button
                        onClick={handleCancelEdit}
                        className="btn-secondary"
                        disabled={savingPrompt}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => handleSavePrompt(operation.operation_type_id)}
                        className="btn-primary"
                        disabled={savingPrompt}
                      >
                        {savingPrompt ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="prompt-preview">
                    <div className="prompt-text">
                      {operation.has_custom_prompt ? (
                        <pre>{operation.custom_prompt}</pre>
                      ) : operation.default_prompt ? (
                        <pre className="default-prompt">{operation.default_prompt}</pre>
                      ) : (
                        <span className="no-prompt">Sin prompt definido</span>
                      )}
                    </div>
                    <div className="prompt-buttons">
                      <button
                        onClick={() => handleEditPrompt(operation)}
                        className="btn-edit-prompt"
                      >
                        {operation.has_custom_prompt ? 'Editar' : 'Personalizar'}
                      </button>
                      {operation.has_custom_prompt && (
                        <button
                          onClick={() => handleResetPrompt(operation.operation_type_id)}
                          className="btn-reset-prompt"
                          disabled={savingPrompt}
                        >
                          Resetear
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PromptsPage;
